import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  ActionMenu,
  colors2,
  LoaderOverlay,
  ModalConfirm,
  PaddingTopBottom5,
  TabBarV2,
  UI_BODY_BOLD_SM_DF,
  UI_BODY_SEMIBOLD_SM,
  UI_BODY_SM_DF,
  UI_TITLE_SM,
  WrapperCardGrid,
  icons,
  TextInput,
  Divider
} from "oolib";
import { ButtonGhost, ButtonPrimary, ButtonSecondary, ButtonTertiaryCompact } from "oolib/dist/v2/components/Buttons";

import { SidebarLayoutV2 } from "@/components/layout/SidebarLayoutV2";
import { useOutOfTheBoxForm } from "@/pageTpls/ContributeTpl/utils/useOutOfTheBoxForm";
import { formValidationCheckV2 } from "@/utils/validation/formValidation";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { AddWidgetButton } from "./comps/AddWidgetButton";
import { WidgetRenderer } from "./comps/WidgetRenderer";
import { DraggableWidget } from "./comps/DraggableWidget";
import {
  allWidgetsBaseConfs,
  processRawConfig,
  sampleConfig,
  tabBarFormRendererOptions,
} from "./CONFIG";
import { GRID_GAP, TOTAL_COLUMNS } from "./constants";
import { useDeleteReport, useViewReport, usePublicReport } from "./queries";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { FormRendererPlaceholder } from "./comps/FormRendererPlaceholder";
import { WidgetConfigFormRenderer } from "./comps/WidgetConfigFormRenderer";
import { ReportsHeader }  from "./comps/ReportsHeader";
import FilterModule from "@/components/discovery/FilterModule";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { 
  StyledFilterHeader, 
  StyledGridContainer, 
  StyledWidgetFormHeader,
  StyledLoadingContainer,
  StyledMainContent,
  StyledSidebarToggleButton,
  StyledAddWidgetButtonContainer,
  Z_INDEX
} from "./styled";
import { isTplAnnotationEnabled } from "@okf/ootils";
import { ensureWidgetIds } from "./utils/ensureWidgetIds";
import { queryClient } from "@/index";
// import { ReportSettingForm } from "./comps/ReportSettingForm";
import OKE401 from "@/pageTpls/OKE401";
import { useHeaderHeight } from "@/CONSTANTS";
import { useAutoGenFilterConfigsFromTpl } from "../../utils/useAutoGenFilterConfigsFromTpl";
import { useDynamicLayoutHeight } from "../../InsightMatrix/utils/useDynamicLayoutHeight";
import {
  ReportLandingContextProvider,
  useWidgetEditing,
  useReportConfig,
  useReportFilters,
  useReportMode,
  useReportMeta,
  useReportModals,
  useReportData,
  useReportSnapshot,
  useReportCollab
} from "./ReportLandingContext";
import { TabBarStyle1, TabBarStyle2 } from "oolib/dist/v2/components/TabBar";
import { FilterSidebar, WidgetFormSidebar } from "./comps/WidgetFormSidebar";

const { Funnel } = icons;

export const ADD_WIDGET_BTN_LEFT_RIGHT_SPACE = 6
export const ADD_WIDGET_BTN_WIDTH = 24
export const ADD_WIDGET_BTN_WIDTH_EMPTY_SCREEN = 84
export const WIDGET_WRAPPER_PADDING = 14
export const ADD_WIDGET_BTN_SECONDARY_HEIGHT = 54

export const ACTIONS_SPACE = ADD_WIDGET_BTN_WIDTH + (ADD_WIDGET_BTN_LEFT_RIGHT_SPACE * 2)
export const ACTIONS_SPACE_WHEN_COLLAPSED = WIDGET_WRAPPER_PADDING + WIDGET_WRAPPER_PADDING + ADD_WIDGET_BTN_LEFT_RIGHT_SPACE
export const ACTIONS_SPACE_READ_ONLY = WIDGET_WRAPPER_PADDING
export const ACTIONS_SPACE_READ_ONLY_WHEN_COLLAPSED = ADD_WIDGET_BTN_LEFT_RIGHT_SPACE + WIDGET_WRAPPER_PADDING

// reportId: optional prop — if provided, overrides the URL param (useful for embedding reports outside the route)
// configTransform: optional fn(config) => config — applied to the fetched report config before rendering.
//   Use this to programmatically modify widget configs (e.g. injecting user-specific filters).
// hideFilterSidebar: optional bool — when true, the report-level FilterSidebar (left filter panel) is not rendered.
//   Useful for embedded contexts where the surrounding page already pre-scopes the data.
// hideBackButton: optional bool — when true, the ReportsHeader's back-to-listing arrow is not rendered.
//   Useful when the report is mounted somewhere with no listing to go back to (e.g. a dashboard tab).
// publicToken: optional — when provided, this renders the PUBLIC read-only snapshot of a
//   shared report (no auth). It fetches by token instead of reportId, feeds each widget its
//   frozen snapshot data, hides filters, and shows a "data as of {date}" banner.
//   See SHARED_REPORTS_DESIGN.md.
// forceReadOnly: optional bool — keeps the report read-only regardless of the user's PUBLISH
//   access by suppressing the collab (always-live) session. Required for embedded contexts
//   that rely on configTransform (e.g. the VolunteerDashboard on the MAD tenant): collab
//   sources config from the live Y.Doc, which would bypass the transform AND grant
//   edit-capable users live edit on the shared report.
export const ReportLanding = ({ reportId: reportIdProp, configTransform, hideFilterSidebar = false, hideBackButton = false, publicToken, forceReadOnly = false } = {}) => {
  const params = useParams();
  const location = useLocation();
  const isPublic = !!publicToken;
  const reportId = reportIdProp || params.reportId;
  // There is one way to open a report: its /view URL. Who may edit is decided
  // by access once the collab session is up, not by the URL. The old /edit
  // URL still lives in bookmarks and old links, so it lands here and is sent
  // on to /view — same path, one segment swapped (both URL shapes carry the
  // segment: /reports/edit/:id and /analysis/reports/:id/edit).
  const isLegacyEditUrl = !reportIdProp && !isPublic && params.mode === "edit";

  // Live (authed) fetch by id, OR public fetch by token — never both.
  const { data: viewData, status: viewStatus, error: viewError } = useViewReport({ reportId, enabled: !isPublic && !isLegacyEditUrl });
  const { data: publicData, status: publicStatus, error: publicError } = usePublicReport({ token: publicToken, enabled: isPublic });

  const rawData = isPublic ? publicData : viewData;
  const status = isPublic ? publicStatus : viewStatus;
  const error = isPublic ? publicError : viewError;

  const data = rawData && configTransform ? { ...rawData, config: configTransform(rawData.config) } : rawData;

  if (isLegacyEditUrl) {
    return <Navigate replace to={location.pathname.replace(/(^|\/)edit(?=\/|$)/, "$1view") + location.search} />;
  }

  return (
    <ReportLandingContextProvider reportId={reportId} isPublicSnapshot={isPublic} forceReadOnly={forceReadOnly}>
      <ReportLandingBody
        data={data}
        status={status}
        error={error}
        reportId={reportId}
        hideFilterSidebar={hideFilterSidebar}
        hideBackButton={hideBackButton}
        isPublic={isPublic}
      />
    </ReportLandingContextProvider>
  );
};

/**
 * Loading gate. Split out of ReportLanding so it can sit INSIDE the context
 * provider and read the collab session.
 *
 * The report isn't ready when its data arrives — it's ready when its data has
 * arrived AND the collab session that owns that data is live. Releasing on the
 * fetch alone renders a page whose config comes from the server snapshot and
 * then swaps wholesale to the Y.Doc a moment later: fields visibly re-render,
 * and anything typed in between is discarded. So the fetch and the sync are one
 * loading state, not two.
 *
 * `collabPending` is already false when collab is off (prod, public snapshot,
 * forceReadOnly embeds), so the legacy flow is never held here.
 */
const ReportLandingBody = ({ data, status, error, reportId, hideFilterSidebar, hideBackButton, isPublic }) => {
  const headerHeight = useHeaderHeight();
  const { collabPending } = useReportCollab();

  if (status === "loading" || collabPending) {
    return (
      <StyledLoadingContainer headerHeight={headerHeight}>
        <WrapperCardGrid>
          {[1, 2, 3, 4, 5, 6].map((d, i) => (
            <div key={`widget-loader-${i}`} style={{ height: "30rem" }} />
          ))}
        </WrapperCardGrid>
      </StyledLoadingContainer>
    );
  }
  if (status === "error") return <OKE401 msg={error?.message} />;
  if (status !== "success") return null;

  return (
    <ReportMainPage
      data={data}
      reportId={reportId}
      hideFilterSidebar={hideFilterSidebar || isPublic}
      hideBackButton={hideBackButton || isPublic}
      isPublicReport={isPublic}
      snapshot={isPublic ? data?.snapshot : undefined}
      sharedAt={isPublic ? data?.sharedAt : undefined}
    />
  );
};

/**
 * ReportLanding Component
 * 
 * Main component for the report landing page that handles:
 * - Report viewing and editing modes
 * - Widget configuration and management
 * - Filter sidebar functionality
 * - Report settings and metadata
 * - Layout and grid system for widgets
 * - Context providers for shared state management
 * - Loading states and error handling
 * - Report publishing and deletion
 */
const ReportMainPage = ({ data, reportId: reportIdProp, hideFilterSidebar = false, hideBackButton = false, isPublicReport = false, snapshot, sharedAt }) => {
  const navigate = useNavigate();
  const params = useParams();
  const reportId = reportIdProp || params.reportId;

  // Initialize context state with data
  const { config, setConfig } = useReportConfig();
  const { readOnly, setReadOnly } = useReportMode();
  const { setUnderEditWidgetId } = useWidgetEditing();
  const { localReportMeta, setLocalReportMeta } = useReportMeta();
  const { showDeleteModal, setShowDeleteModal } = useReportModals();
  const { setReportData } = useReportData();
  const { setSnapshotData, setIsPublicReport } = useReportSnapshot();
  const { collabActive } = useReportCollab();

  // Public-share replay: seed the captured snapshot so WidgetRenderer feeds each widget its
  // frozen data (prefetchedData) instead of fetching. null in normal (live) mode.
  useEffect(() => {
    setSnapshotData(snapshot || null);
  }, [snapshot, setSnapshotData]);

  // Public-report mode: gates public-specific UI (hidden references, AI-summary/regenerate
  // controls, share button, etc.). Distinct from snapshotData/prefetchedData.
  useEffect(() => {
    setIsPublicReport(isPublicReport);
  }, [isPublicReport, setIsPublicReport]);

  /**
   * Initialize user-editable local state (config, meta) only when switching to a different report.
   * We key off reportId instead of the full data object to prevent resetting local changes
   * when react-query refetches the same report (e.g., on window focus).
   * This ensures unsaved edits (like hidden bar selections) aren't lost on refetch.
   */
  useEffect(() => {
    // In collab mode config + meta come from the live Y.Doc (loaded server-side
    // into the session), so seeding local state would write the server snapshot
    // back over live edits via applyConfig. Skip it — the session is the source.
    if (collabActive) return;
    // Backfill ids on any legacy id-less widgets so under-edit tracking (keyed by
    // stable widget id, not array index) is safe. Self-heals on the next save.
    setConfig(ensureWidgetIds(data.config));
    setLocalReportMeta({ title: data.title, subtitle: data.subtitle });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, collabActive]);

  // Sync derived/read-only state whenever the data changes. Outside a collab
  // session (public snapshot, embed, no access) a report is only ever read:
  // the legacy save path is gone, so there is nothing an edit could do.
  useEffect(() => {
    setReadOnly(true);
    setReportData(data);
  }, [data, setReadOnly, setReportData]);

  // Local component state (not shared across components)
  const [ formRendererOption, setFormRendererOption ] = useState(tabBarFormRendererOptions[0]) 
  const [ sidebarToggleState, setSidebarToggleState ] = useState({ lsb: true, rsb: false }) 

  // Read-only readers get filters in that sidebar, not widget config — start it
  // collapsed so the report has the full width. Keyed off the flip rather than
  // the initial state: readOnly is resolved a render later (collab access),
  // and expanding it afterwards sticks, since this only runs when readOnly
  // itself changes.
  useEffect(() => {
    if (readOnly) setSidebarToggleState(prev => ({ ...prev, lsb: false }));
  }, [readOnly]);
  
  const handleSetUnderEditWidgetId = (id) => {
    if(id !== undefined){
      // open sidebar when edit is clicked
      setSidebarToggleState({ lsb: true, rsb: false})
      // When a widget is taken into edit, switch sidebar tab to widget config
      if(formRendererOption.value !== 'widget'){
        setFormRendererOption(tabBarFormRendererOptions.find(op => op.value === 'widget'))
      }
    }
    setUnderEditWidgetId(id);
  }
  
  // states
  const userData = queryClient.getQueryData("userData")

  const { mutate: deleteReport, deleteIsLoading } = useDeleteReport();

  const allTpls = useGetQueryData('allTpls')
  
  // Helper function to get selected templates based on config
  // TODO: This logic needs to be reviewed and fixed
  const getSelectedTpls = ({ config, allTpls }) => {
    if (!config?.length) return [];
    
    // Process raw configs to make them easier to work with
    const processedConfigs = config.map(c => {
      const processor = processRawConfig[c.comp]
      if(processor){
        const processed = processor(c);
        return processed;
      }else{
        return c
      }
    })

    // one template per widget (`contentType`), or several for a widget that
    // switches between templates (`contentTypes`, see WidgetTemp_SHI)
    let allCTs = processedConfigs.flatMap(c => [c.props?.contentType, ...(c.props?.contentTypes || [])]).filter(Boolean)
    allCTs = Array.from(new Set(allCTs)) // Remove duplicates

    if(allCTs.includes('annotations')){
      // Get publishing-segment tpls that have at least one annotation-capable block
      return allTpls.filter(isTplAnnotationEnabled)
    } else {
      // Get templates for content types excluding annotations
      return allCTs
        .filter(ct => ct !== 'annotations')
        .map(ct => allTpls.find(tpl => tpl.kp_content_type === ct))
        .filter(Boolean)
    }
  };


  // Memoize expensive filter config calculation
  const selectedTpls = useMemo(() => 
    getSelectedTpls({ config, allTpls }), 
    [config, allTpls]
  );
  
  const filterConfig = useAutoGenFilterConfigsFromTpl({
    selectedTpls,
    filterScopes:['doc', 'anno']
  });

  // Platform configuration and navigation
  const {SELF_MANAGED_BASE_CONFIGS} = useGetQueryData('platformConfigs')
  const navigateBackToReportsListing = useCallback(() => {
    navigate(
      SELF_MANAGED_BASE_CONFIGS?.reports?.linkToReportsListing ||
      '/reports'
    );
  }, [navigate, SELF_MANAGED_BASE_CONFIGS]);

  // Layout calculations - memoized to prevent recalculation on every render
  const {parentWrapperRef, gridHeightCSS} = useDynamicLayoutHeight()
  const paddingLeft = useMemo(() => 
    readOnly 
      ? !sidebarToggleState?.lsb ? ACTIONS_SPACE_READ_ONLY_WHEN_COLLAPSED : ACTIONS_SPACE_READ_ONLY 
      : !sidebarToggleState?.lsb ? ACTIONS_SPACE_WHEN_COLLAPSED : ACTIONS_SPACE,
    [ readOnly, sidebarToggleState?.lsb]
  );

  const isEmptyScreen = useMemo(() => !config || config.length === 0, [config]);

  // Memoize sidebar toggle callbacks
  const handleSidebarToggle = useCallback(() => {
    setSidebarToggleState(prev => ({ ...prev, lsb: true }));
  }, [setSidebarToggleState]);

  // Memoize sidebar component to prevent recreation on every render
  const sidebarComponent = useMemo(() => 
    readOnly  
      ? <div style={{ width: "100%", 
                      ...(!sidebarToggleState.lsb && {display: "flex", justifyContent: "flex-end"})  }}>
          {sidebarToggleState.lsb 
            ? <FilterSidebar
                filterConfig={filterConfig}
                setSidebarToggleState={setSidebarToggleState}
                reportId={reportId}
                showSidebarCollapseButton={true}
                viewMode
              />
            : 
            <div style={{ padding: "0.4rem 1rem" }}>
              <ButtonGhost
                icon={sidebarToggleState?.lsb ? "ArrowLineLeft" : "ArrowLineRight"}
                onClick={handleSidebarToggle}
              />
            </div>
          }
        </div>
      : 
        <WidgetFormSidebar 
          formRendererOption={formRendererOption}
          sidebarToggleState={sidebarToggleState}
          setFormRendererOption={setFormRendererOption}
          setSidebarToggleState={setSidebarToggleState}
          data={data}
          localReportMeta={localReportMeta}
          setLocalReportMeta={setLocalReportMeta}
          setShowDeleteModal={setShowDeleteModal}
          filterConfig={filterConfig}
        />,
    [ 
      readOnly, 
      filterConfig, 
      setSidebarToggleState, 
      setFormRendererOption, 
      formRendererOption, 
      data, 
      localReportMeta, 
      setLocalReportMeta, 
      setShowDeleteModal
    ]
  );

  return (
    <div>
      {/* Loader Overlays */}
      {deleteIsLoading && <LoaderOverlay loaderText="Deleting Report..."/>}
      
      {/* Modal Dialogs */}
      {showDeleteModal && (
        <ModalConfirm
          title="Are you sure you want to delete this report?"
          onClose={() => setShowDeleteModal(false)}
          onCloseText="No"
          onConfirmText="Yes, delete this report"
          onConfirm={() => {
            deleteReport({ reportId: data._id, title: data.title }, {
              onSuccess: () => navigate('/reports')
            });
          }}
        />
      )}
      
      {/* Header */}
      <div>
        <ReportsHeader
          navigateBackToReportsListing={navigateBackToReportsListing}
          hideBackButton={hideBackButton}
        />
      </div>

      {/* Public-share snapshot banner — sets the expectation that this is a point-in-time view */}
      {sharedAt && (
        <div style={{
          padding: "0.8rem 1.6rem",
          background: colors2.grey5,
          borderBottom: `1px solid ${colors2.grey10}`,
        }}>
          <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey60 }}>
            {`Shared snapshot — data as of ${new Date(sharedAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}`}
          </UI_BODY_SEMIBOLD_SM>
        </div>
      )}

      {/* Layout */}
      <div ref={parentWrapperRef}>
        <SidebarLayoutV2
          gridHeightCSS={gridHeightCSS}
          toggleState={sidebarToggleState}
          lsbComp={hideFilterSidebar ? null : sidebarComponent}
          lsbWidth={hideFilterSidebar ? 0 : 335}
          lsbClosedWidth={hideFilterSidebar ? 0 : 50}
        >
          <StyledMainContent 
            padding={WIDGET_WRAPPER_PADDING}
            paddingLeft={paddingLeft}
            minHeight={gridHeightCSS}
            $viewMode={readOnly}
          >
            {/* Sidebar Toggle Button (when collapsed) */}
            {/* {!sidebarToggleState?.lsb && 
              <StyledSidebarToggleButton left={WIDGET_WRAPPER_PADDING} top={0}>
                <ButtonGhost 
                  icon={readOnly ? "Funnel" : "TextIndent"} 
                  onClick={handleSidebarToggle}
                  style={{
                    width: `${ADD_WIDGET_BTN_WIDTH}px`,
                    height: `${ADD_WIDGET_BTN_WIDTH}px`,
                    background: colors2.white,
                    top: WIDGET_WRAPPER_PADDING
                  }}
                />
              </StyledSidebarToggleButton>
            } */}
            
            {/* Widgets Grid */}
            {config.length > 0 && (
              readOnly ? (
                <StyledGridContainer gap={GRID_GAP} columns={TOTAL_COLUMNS}>
                  {config.map((item, itemIdx) => (
                    <WidgetRenderer
                      key={`widget-renderer-${itemIdx}`}
                      widgetConfig={item}
                      widgetIdx={itemIdx}
                      setUnderEditWidgetId={handleSetUnderEditWidgetId}
                    />
                  ))}
                </StyledGridContainer>
              ) : (
                <DndProvider backend={HTML5Backend}>
                  <StyledGridContainer gap={GRID_GAP} columns={TOTAL_COLUMNS}>
                    {config.map((item, itemIdx) => (
                      <DraggableWidget
                        key={`widget-draggable-${item.id}`}
                        widgetIdx={itemIdx}
                        widgetConfig={item}
                        setUnderEditWidgetId={handleSetUnderEditWidgetId}
                      >
                        <WidgetRenderer
                          widgetConfig={item}
                          widgetIdx={itemIdx}
                          setUnderEditWidgetId={handleSetUnderEditWidgetId}
                        />
                      </DraggableWidget>
                    ))}
                  </StyledGridContainer>
                </DndProvider>
              )
            )}
            
            {/* Add Widget Button (bottom) */}
            {!readOnly && (
              <StyledAddWidgetButtonContainer>
                <AddWidgetButton
                  isNewRow={true}
                  setUnderEditWidgetId={handleSetUnderEditWidgetId}
                  isEmptyScreen={isEmptyScreen}
                  variant="secondary"
                  style={{ 
                    marginLeft: !isEmptyScreen ? "0" : `-${ADD_WIDGET_BTN_WIDTH}px` 
                  }}
                />
              </StyledAddWidgetButtonContainer>
            )}
          </StyledMainContent>
        </SidebarLayoutV2>
      </div>
    </div>
  );
};